import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ChevronUp, ChevronDown, Instagram, Globe2, Send, PlayCircle, Music2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_20601db6-8b59-495a-899a-ce4625f47158/artifacts/u4f6xq9k_image.png";
const EMPRENOVUS_LOGO = "https://customer-assets.emergentagent.com/job_impermeabiliza-ya/artifacts/g5ryp6cs_image.png";

function PrivacyCookieTab() {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(() => localStorage.getItem("cookies_accepted") === "true");

  if (accepted) return null;

  return (
    <div data-testid="privacy-cookie-tab" className="fixed bottom-0 left-6 z-50" style={{ maxWidth: "380px" }}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="privacy-toggle-btn"
        className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-zinc-300 text-xs font-medium border border-zinc-700 border-b-0 rounded-t-md hover:bg-zinc-800 transition-colors"
      >
        {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        Privacidad y Cookies
      </button>
      {open && (
        <div className="bg-zinc-900 border border-zinc-700 border-t-0 p-5 text-xs text-zinc-400 leading-relaxed">
          <h4 className="text-white font-bold text-sm mb-3">Política de Privacidad y Cookies</h4>
          <p className="mb-3">
            Este sitio web utiliza cookies propias y de terceros para mejorar su experiencia de navegación,
            analizar el tráfico del sitio y personalizar el contenido. Al continuar navegando, usted acepta
            el uso de cookies de acuerdo con nuestra política.
          </p>
          <p className="mb-3">
            <strong className="text-zinc-300">Datos personales:</strong> La información proporcionada a través de
            formularios de contacto y descarga de fichas técnicas (nombre, correo electrónico, teléfono) será
            utilizada exclusivamente para fines comerciales de Construtop y no será compartida con terceros
            sin su consentimiento.
          </p>
          <p className="mb-4">
            <strong className="text-zinc-300">Sus derechos:</strong> Puede solicitar la modificación o eliminación
            de sus datos personales contactándonos a{" "}
            <a href="mailto:comercial@construtop.com.ec" className="text-emerald-400 hover:underline">comercial@construtop.com.ec</a>.
          </p>
          <button
            onClick={() => { localStorage.setItem("cookies_accepted", "true"); setAccepted(true); }}
            data-testid="accept-cookies-btn"
            className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider transition-colors"
          >
            Aceptar
          </button>
        </div>
      )}
    </div>
  );
}

export { PrivacyCookieTab };

export default function Footer() {
  return (
    <>
    <footer data-testid="main-footer" className="bg-zinc-950 text-zinc-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <img src={LOGO_URL} alt="Construtop" className="h-10 mb-6" />
            <p className="text-sm leading-relaxed">
              Expertos en impermeabilización y acabados con más de 15 años de experiencia en Ecuador.
              Soluciones integrales para el sector industrial, comercial y residencial.
            </p>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Soluciones</h4>
            <ul className="space-y-3 text-sm">
              {["Cubiertas", "Cisternas", "Jardineras", "Terrazas", "Muros"].map((item) => (
                <li key={item}>
                  <Link to={`/soluciones`} className="hover:text-emerald-500 transition-colors">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Navegación</h4>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Sistemas", path: "/sistemas" },
                { label: "Fichas Técnicas", path: "/fichas-tecnicas" },
                { label: "Proyectos", path: "/proyectos" },
                { label: "Blog", path: "/blog" },
                { label: "Distribuidores", path: "/distribuidores" },
              ].map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="hover:text-emerald-500 transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Contacto</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-emerald-500 shrink-0" />
                <a href="tel:+593999307185" className="hover:text-emerald-500 transition-colors">+593 999 307 185</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-emerald-500 shrink-0" />
                <a href="mailto:comercial@construtop.com.ec" className="hover:text-emerald-500 transition-colors">comercial@construtop.com.ec</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>Quito, Ecuador</span>
              </li>
            </ul>
            <div className="mt-6">
              <h5 className="text-white font-bold text-xs uppercase tracking-wider mb-3">Redes sociales</h5>
              <div className="flex gap-2" aria-label="Redes sociales pendientes de configuración">
                {[
                  { label: "Instagram", icon: Instagram },
                  { label: "Facebook", icon: Globe2 },
                  { label: "LinkedIn", icon: Send },
                  { label: "YouTube", icon: PlayCircle },
                  { label: "TikTok", icon: Music2 },
                ].map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    title={`${label} pendiente de enlace oficial`}
                    className="w-9 h-9 border border-zinc-800 text-zinc-500 hover:text-emerald-400 hover:border-emerald-700 transition-colors flex items-center justify-center"
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800 mt-12 pt-8 flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <p className="text-xs">&copy; 2026 Construtop. Todos los derechos reservados.</p>
            <span className="text-zinc-700">|</span>
            <a
              href="https://www.emprenovus.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="emprenovus-credit-link"
              className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <img src={EMPRENOVUS_LOGO} alt="Emprenovus" className="h-4" />
              AI powered by Emprenovus
            </a>
          </div>
        </div>
      </div>
    </footer>
    <PrivacyCookieTab />
    </>
  );
}
